import { useState, useEffect } from 'react';
import {
    FileText,
    Plus,
    Trash2,
    CheckCircle,
    XCircle,
    Clock,
    Send,
    Eye,
} from 'lucide-react';
import { useAuthStore } from '@dukene/shared';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { confirmAction } from '../../components/modals/ConfirmModal';

const templateSchema = z.object({
    name: z.string().min(1, 'Template name is required'),
    language: z.string().min(1, 'Language is required'),
    category: z.enum(['MARKETING', 'UTILITY', 'AUTHENTICATION']),
    header: z.string().optional(),
    body: z.string().min(1, 'Template body is required'),
    footer: z.string().optional(),
    buttons: z.array(z.any()).optional(),
});

type TemplateFormData = z.infer<typeof templateSchema>;

interface Template {
    id: string;
    name: string;
    language: string;
    category: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
    header?: string;
    body: string;
    footer?: string;
    buttons?: any[];
    status: 'PENDING' | 'APPROVED' | 'REJECTED';
    variables: string[];
    createdAt: Date;
}

const WhatsAppTemplates = () => {
    const { user } = useAuthStore();
    const [templates, setTemplates] = useState<Template[]>([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
    const [saving, setSaving] = useState(false);

    const {
        register,
        handleSubmit,
        reset,
        watch,
        formState: { errors },
    } = useForm<TemplateFormData>({
        resolver: zodResolver(templateSchema),
        defaultValues: {
            language: 'en',
            category: 'UTILITY',
        },
    });

    const bodyText = watch('body');

    useEffect(() => {
        fetchTemplates();
    }, []);

    const fetchTemplates = async () => {
        setLoading(true);
        try {
            const response = await fetch(`/api/v1/whatsapp/templates/${user?.storeId}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                setTemplates(data);
            }
        } catch (error) {
            console.error('Error fetching templates:', error);
        } finally {
            setLoading(false);
        }
    };

    const onSubmit = async (data: TemplateFormData) => {
        setSaving(true);
        try {
            // Extract variables from body text ({{1}}, {{2}}, etc.)
            const variables: string[] = [];
            const variableMatches = data.body.match(/\{\{(\d+)\}\}/g);
            if (variableMatches) {
                variableMatches.forEach((match) => {
                    const num = match.replace(/\{\{|\}\}/g, '');
                    variables.push(`var_${num}`);
                });
            }

            const response = await fetch(`/api/v1/whatsapp/templates/${user?.storeId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
                body: JSON.stringify({
                    ...data,
                    variables,
                }),
            });

            if (response.ok) {
                await fetchTemplates();
                setShowCreateModal(false);
                reset();
                alert('Template created successfully! It will be submitted to WhatsApp for approval.');
            } else {
                alert('Failed to create template');
            }
        } catch (error) {
            console.error('Error creating template:', error);
            alert('Error creating template');
        } finally {
            setSaving(false);
        }
    };

    const deleteTemplate = async (templateId: string) => {
        const confirmed = await confirmAction({
            title: 'Delete Template',
            message: 'Are you sure you want to delete this template?',
            confirmText: 'Delete',
            cancelText: 'Cancel',
        });
        if (!confirmed) return;

        try {
            const response = await fetch(`/api/v1/whatsapp/templates/${templateId}`, {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
            });

            if (response.ok) {
                await fetchTemplates();
            } else {
                alert('Failed to delete template');
            }
        } catch (error) {
            console.error('Error deleting template:', error);
            alert('Error deleting template');
        }
    };

    const getStatusBadge = (status: Template['status']) => {
        const styles = {
            PENDING: 'bg-yellow-100 text-yellow-700',
            APPROVED: 'bg-green-100 text-green-700',
            REJECTED: 'bg-red-100 text-red-700',
        };

        const icons = {
            PENDING: Clock,
            APPROVED: CheckCircle,
            REJECTED: XCircle,
        };

        const Icon = icons[status];

        return (
            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
                <Icon className="w-3 h-3" />
                {status}
            </span>
        );
    };

    const getCategoryBadge = (category: Template['category']) => {
        const styles = {
            MARKETING: 'bg-purple-100 text-purple-700',
            UTILITY: 'bg-blue-100 text-blue-700',
            AUTHENTICATION: 'bg-orange-100 text-orange-700',
        };

        return (
            <span className={`px-2 py-1 rounded text-xs font-medium ${styles[category]}`}>
                {category}
            </span>
        );
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-8">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">WhatsApp Templates</h1>
                        <p className="text-gray-600 mt-1">Create and manage message templates for WhatsApp Business</p>
                    </div>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                    >
                        <Plus className="w-5 h-5" />
                        Create Template
                    </button>
                </div>
            </div>

            {/* Info Banner */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex gap-3">
                    <FileText className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                        <h3 className="font-semibold text-blue-900 mb-1">About WhatsApp Templates</h3>
                        <p className="text-sm text-blue-800">
                            Templates must be approved by WhatsApp before use. Use variables like {'{{'}{1}{'}'}, {'{{'}{2}{'}'} for dynamic content.
                            Approval typically takes 24-48 hours.
                        </p>
                    </div>
                </div>
            </div>

            {/* Templates Grid */}
            {templates.length === 0 ? (
                <div className="bg-white rounded-lg shadow p-12 text-center">
                    <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No templates yet</h3>
                    <p className="text-gray-600 mb-6">Create your first WhatsApp message template to get started</p>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 inline-flex items-center gap-2"
                    >
                        <Plus className="w-5 h-5" />
                        Create Template
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {templates.map((template) => (
                        <div key={template.id} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow">
                            <div className="p-6">
                                {/* Header */}
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex-1">
                                        <h3 className="font-semibold text-gray-900 mb-1">{template.name}</h3>
                                        <div className="flex items-center gap-2">
                                            {getCategoryBadge(template.category)}
                                            {getStatusBadge(template.status)}
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setSelectedTemplate(template)}
                                        className="p-2 hover:bg-gray-100 rounded-lg"
                                    >
                                        <Eye className="w-4 h-4 text-gray-600" />
                                    </button>
                                </div>

                                {/* Template Preview */}
                                <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
                                    {template.header && (
                                        <p className="font-semibold text-gray-900 mb-2">{template.header}</p>
                                    )}
                                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{template.body}</p>
                                    {template.footer && (
                                        <p className="text-xs text-gray-500 mt-2">{template.footer}</p>
                                    )}
                                </div>

                                {/* Variables */}
                                {template.variables.length > 0 && (
                                    <div className="mb-4">
                                        <p className="text-xs text-gray-600 mb-1">Variables:</p>
                                        <div className="flex flex-wrap gap-1">
                                            {template.variables.map((variable, index) => (
                                                <span key={index} className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">
                                                    {'{{'}{index + 1}{'}}'} = {variable}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Actions */}
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => deleteTemplate(template.id)}
                                        className="flex-1 px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 flex items-center justify-center gap-2 text-sm"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create Template Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b sticky top-0 bg-white">
                            <h2 className="text-2xl font-bold text-gray-900">Create WhatsApp Template</h2>
                            <p className="text-gray-600 text-sm mt-1">Templates require WhatsApp approval before use</p>
                        </div>

                        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
                            {/* Template Name */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Template Name *
                                </label>
                                <input
                                    type="text"
                                    {...register('name')}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    placeholder="order_confirmation"
                                />
                                {errors.name && (
                                    <p className="text-red-600 text-sm mt-1">{errors.name.message}</p>
                                )}
                                <p className="text-xs text-gray-500 mt-1">Use lowercase with underscores (e.g., order_confirmation)</p>
                            </div>

                            {/* Category and Language */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Category *
                                    </label>
                                    <select
                                        {...register('category')}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="UTILITY">Utility</option>
                                        <option value="MARKETING">Marketing</option>
                                        <option value="AUTHENTICATION">Authentication</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Language
                                    </label>
                                    <select
                                        {...register('language')}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="en">English</option>
                                        <option value="sw">Swahili</option>
                                    </select>
                                </div>
                            </div>

                            {/* Header (Optional) */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Header (Optional)
                                </label>
                                <input
                                    type="text"
                                    {...register('header')}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    placeholder="Order Confirmation"
                                />
                            </div>

                            {/* Body */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Message Body *
                                </label>
                                <textarea
                                    {...register('body')}
                                    rows={6}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    placeholder="Hi {{1}}, your order {{2}} has been confirmed. Total: {{3}}"
                                />
                                {errors.body && (
                                    <p className="text-red-600 text-sm mt-1">{errors.body.message}</p>
                                )}
                                <p className="text-xs text-gray-500 mt-1">
                                    Use {'{{'}{1}{'}'}, {'{{'}{2}{'}'}, etc. for dynamic variables
                                </p>
                            </div>

                            {/* Footer (Optional) */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Footer (Optional)
                                </label>
                                <input
                                    type="text"
                                    {...register('footer')}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    placeholder="Thank you for shopping with us!"
                                />
                            </div>

                            {/* Preview */}
                            {bodyText && (
                                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                    <p className="text-xs font-medium text-gray-600 mb-2">Preview:</p>
                                    <div className="bg-white rounded-lg p-4 shadow-sm">
                                        <p className="text-sm whitespace-pre-wrap">{bodyText}</p>
                                    </div>
                                </div>
                            )}

                            {/* Actions */}
                            <div className="flex justify-end gap-3 pt-4 border-t">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowCreateModal(false);
                                        reset();
                                    }}
                                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 flex items-center gap-2"
                                >
                                    {saving ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                            Creating...
                                        </>
                                    ) : (
                                        <>
                                            <Send className="w-4 h-4" />
                                            Create Template
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Template Detail Modal */}
            {selectedTemplate && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-2xl w-full">
                        <div className="p-6 border-b">
                            <div className="flex items-start justify-between">
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-900">{selectedTemplate.name}</h2>
                                    <div className="flex items-center gap-2 mt-2">
                                        {getCategoryBadge(selectedTemplate.category)}
                                        {getStatusBadge(selectedTemplate.status)}
                                    </div>
                                </div>
                                <button
                                    onClick={() => setSelectedTemplate(null)}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <XCircle className="w-6 h-6" />
                                </button>
                            </div>
                        </div>

                        <div className="p-6">
                            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                                {selectedTemplate.header && (
                                    <p className="font-semibold text-gray-900 mb-3 text-lg">{selectedTemplate.header}</p>
                                )}
                                <p className="text-gray-700 whitespace-pre-wrap mb-3">{selectedTemplate.body}</p>
                                {selectedTemplate.footer && (
                                    <p className="text-sm text-gray-500 border-t pt-3">{selectedTemplate.footer}</p>
                                )}
                            </div>

                            {selectedTemplate.variables.length > 0 && (
                                <div className="mt-4">
                                    <p className="text-sm font-medium text-gray-700 mb-2">Variables:</p>
                                    <div className="flex flex-wrap gap-2">
                                        {selectedTemplate.variables.map((variable, index) => (
                                            <span key={index} className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm">
                                                {'{{'}{index + 1}{'}'} = {variable}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WhatsAppTemplates;
